import { inject, Injectable, Injector } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { SiteStore } from '@core/site/site.store';
import { RESOLVED_TENANT } from '@core/tokens/resolved-tenant.token';
import type { PageTitle } from '@abook-ui/components/page/page-title';

const ROUTE_ROBOTS_ATTR = 'data-route-robots';

/** When neither the route nor the tenant's subtitle says anything. `{site}` is the site name. */
const DEFAULT_DESCRIPTION = 'Find steder, aktiviteter og foreninger hos {site}.';

/**
 * "<page> | <site>": the route's static title, or a page's own once it has loaded (#690). The meta
 * description and canonical too (#904): the route's `data.description` (`{site}` is replaced) or
 * the page's own, and the request's origin plus the path, keeping only `page` from the query.
 * Also the robots meta: a route's `data.robots` (e.g. 'noindex' on pages that only work for one
 * visitor, #906), removed again on pages without it. A tenant that is not indexable (not launched
 * yet, or never, like showcase) gets noindex on every page (#1039, #1106).
 * Page 2 and on of a list (`?page=N`) say so in title and description: "Foreninger – side 2", so
 * Search Console does not report every page of a list as a duplicate of the first.
 * See .claude/specs/abook-customer-app/core/seo-meta.md.
 */
@Injectable({ providedIn: 'root' })
export class AbookTitleStrategy extends TitleStrategy implements PageTitle {
  // SiteStore injects Router, which creates a SiteStore → Router → TitleStrategy → SiteStore
  // cycle at construction time. Resolve lazily via Injector instead.
  private readonly injector = inject(Injector);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly tenantIndexable = inject(RESOLVED_TENANT).indexable !== false;
  // A noindex already in index.html holds on every page; routes never lift it.
  // Tags this strategy wrote carry ROUTE_ROBOTS_ATTR, so a server-rendered page's route noindex,
  // still in the DOM at hydration, is not mistaken for one from index.html.
  private readonly noindexFromHtml = (() => {
    const tag = this.meta.getTag('name="robots"');
    return !!tag && !tag.hasAttribute(ROUTE_ROBOTS_ATTR) && /noindex/i.test(tag.content);
  })();
  /** The current URL's list page from 2 on, else null; pages that set their own title get it too. */
  private listPage: string | null = null;

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.listPage = listPageOf(new URL(snapshot.url, 'http://x').searchParams.get('page'));
    this.setPageTitle(this.buildTitle(snapshot));
    const noindex = this.setRobots(snapshot.root);
    this.setPageDescription(this.routeDescription(snapshot.root));
    this.setCanonical(noindex ? null : snapshot.url);
  }

  setPageTitle(pageTitle: string | undefined): void {
    const siteName = this.siteName();
    const page = pageTitle && this.listPage ? `${pageTitle} – side ${this.listPage}` : pageTitle;
    const fullTitle = page ? `${page} | ${siteName}` : siteName;
    this.title.setTitle(fullTitle);
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
  }

  /**
   * Noindex on the current page, and no canonical: a page whose content was deleted since the
   * static render (#999). The next navigation sets robots again from its route.
   */
  setPageNoindex(): void {
    if (!this.noindexFromHtml) this.meta.updateTag({ name: 'robots', content: 'noindex', [ROUTE_ROBOTS_ATTR]: '' });
    this.setCanonical(null);
  }

  setPageDescription(description: string): void {
    // A description without a full stop gets one, so "Side N." starts its own sentence.
    const content = this.listPage ? `${description.replace(/(?<![.!?…])\s*$/, '.')} Side ${this.listPage}.` : description;
    this.meta.updateTag({ name: 'description', content });
    this.meta.updateTag({ property: 'og:description', content });
  }

  /** Returns whether the page is noindex. */
  private setRobots(root: ActivatedRouteSnapshot): boolean {
    if (this.noindexFromHtml) return true;
    // The deepest route that says something wins; data on a parent covers its children.
    let robots: string | undefined = this.deepest(root, 'robots');
    if (!this.tenantIndexable) robots = 'noindex';
    if (robots) this.meta.updateTag({ name: 'robots', content: robots, [ROUTE_ROBOTS_ATTR]: '' });
    else this.meta.removeTag('name="robots"');
    return !!robots && /noindex/i.test(robots);
  }

  private routeDescription(root: ActivatedRouteSnapshot): string {
    const site = this.injector.get(SiteStore).settings();
    const template = this.deepest(root, 'description') || site?.subtitle?.trim() || DEFAULT_DESCRIPTION;
    return template.replace(/\{site\}/g, this.siteName());
  }

  /**
   * The request's origin plus the path, never the tenant config's host (#904): a copied config
   * must not point a new tenant's pages at another. Only `page` survives from the query, and only
   * from page 2 — filters would make every combination its own URL (#903). None on noindex pages.
   */
  private setCanonical(url: string | null): void {
    const existing = this.document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (url === null) {
      existing?.remove();
      this.meta.removeTag('property="og:url"');
      return;
    }
    const origin = this.document.location.origin;
    const parsed = new URL(url, origin);
    const page = listPageOf(parsed.searchParams.get('page'));
    const canonical = `${origin}${parsed.pathname}${page ? `?page=${page}` : ''}`;
    const link = existing ?? this.document.createElement('link');
    if (!existing) {
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
    this.meta.updateTag({ property: 'og:url', content: canonical });
  }

  private deepest(root: ActivatedRouteSnapshot, key: string): string | undefined {
    let value: string | undefined;
    for (let route: ActivatedRouteSnapshot | null = root; route; route = route.firstChild) {
      value = (route.data[key] as string | undefined) ?? value;
    }
    return value;
  }

  private siteName(): string {
    return this.injector.get(SiteStore).settings()?.name ?? 'Abook';
  }
}

/** A list page worth its own URL and title: 2 and on, written plainly (not 02). */
function listPageOf(page: string | null): string | null {
  return page && /^(?:[2-9]|[1-9]\d+)$/.test(page) ? page : null;
}
